import { api } from './api-client';

// ============================================================================
// Types
// ============================================================================

export interface Question {
  id: string;
  type: 'circle' | 'write' | 'draw' | 'count' | 'compare';
  prompt: string;
  options?: string[];
  answer_key?: string | string[];
  clipart?: string | string[];
}

export interface Worksheet {
  id: string;
  title: string;
  grade: string;
  subject: string;
  outcome_ids: string[];
  difficulty?: number;
  estimated_minutes?: number;
  questions?: Question[];
}

export interface WorksheetFilters {
  grade?: string;
  subject?: string;
}

export interface WorksheetListResponse {
  items: Worksheet[];
  total: number;
}

// ============================================================================
// Service
// ============================================================================

class WorksheetService {
  /**
   * Get all worksheets with optional filtering.
   * Public endpoint - no auth required.
   */
  async getWorksheets(
    filters?: WorksheetFilters,
    signal?: AbortSignal
  ): Promise<WorksheetListResponse> {
    const params = new URLSearchParams();

    if (filters?.grade) {
      params.append('grade', filters.grade);
    }
    if (filters?.subject) {
      params.append('subject', filters.subject);
    }

    const queryString = params.toString();
    const url = queryString ? `/worksheets?${queryString}` : '/worksheets';

    return api.get<WorksheetListResponse>(url, { signal });
  }

  /**
   * Get a specific worksheet by ID.
   * Public endpoint - no auth required.
   * Returns worksheet WITHOUT answer_key for questions.
   */
  async getWorksheet(id: string, signal?: AbortSignal): Promise<Worksheet> {
    return api.get<Worksheet>(`/worksheets/${id}`, { signal });
  }

  /**
   * Get the URL for a worksheet's PDF.
   * Does not make a request - just constructs the URL for the browser to load directly.
   */
  getWorksheetPdfUrl(id: string): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8082';
    return `${baseUrl}/worksheets/${id}/pdf`;
  }

  /**
   * Get a worksheet with answer key included.
   * Requires authentication - only for teachers.
   */
  async getWorksheetWithAnswers(
    id: string,
    signal?: AbortSignal
  ): Promise<Worksheet> {
    return api.get<Worksheet>(`/worksheets/${id}/answer-key`, { signal });
  }
}

export const worksheetService = new WorksheetService();
